const ISO_WALL =
  /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

export type WallClock = {
  day: string;
  month: string;
  hours: string;
  minutes: string;
  instant: number;
};

export const parseWallClock = (value: string | null): WallClock | undefined => {
  if (value == null) {
    return undefined;
  }

  const match = ISO_WALL.exec(value);
  if (match == null || match[1] == null || match[2] == null || match[3] == null) {
    return undefined;
  }

  const instant = Date.parse(value);
  if (Number.isNaN(instant)) {
    return undefined;
  }

  return {
    day: match[1],
    month: match[1].slice(0, 7),
    hours: match[2],
    minutes: match[3],
    instant,
  };
};

/** Advertised day and clock, ordered as written, ignoring the offset. */
export const wallOrder = (clock: WallClock): number => {
  const [year, month, date] = clock.day.split('-').map(Number);
  return Date.UTC(year ?? 0, (month ?? 1) - 1, date ?? 1, Number(clock.hours), Number(clock.minutes));
};

/** Letter abbreviations as-is; a numeric offset such as "+1030" becomes "UTC+1030". */
export const displayTimezone = (value: string): string => {
  if (/^[A-Za-z]+$/.test(value)) {
    return value;
  }

  return /^[+-]/.test(value) ? `UTC${value}` : '';
};

export const monthGroupLabel = (month: string, lang: string): string => {
  const [year, monthIndex] = month.split('-').map(Number);
  if (year == null || monthIndex == null || Number.isNaN(year) || Number.isNaN(monthIndex)) {
    return month;
  }

  const locale = pageLocale(lang);
  const formatted = new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, monthIndex - 1, 1)));

  return formatted.charAt(0).toLocaleUpperCase(locale) + formatted.slice(1);
};

const isMidnight = (clock: WallClock): boolean =>
  clock.hours === '00' && clock.minutes === '00';

const clockLabel = (clock: WallClock): string => `${clock.hours}:${clock.minutes}`;

export const formatWhen = (
  start: WallClock,
  end: WallClock | undefined,
  timezone: string,
  copy: { from: string; to: string; at: string },
  lang: string,
): string => {
  const locale = pageLocale(lang);
  const zone = displayTimezone(timezone);
  const startMidnight = isMidnight(start);
  const endMidnight = end == null || isMidnight(end);

  if (end == null) {
    const day = formatDay(start.day, locale, 'full');
    return startMidnight ? day : appendClock(day, clockLabel(start), zone);
  }

  if (start.day === end.day) {
    const day = formatDay(start.day, locale, 'full');
    if (startMidnight && endMidnight) {
      return day;
    }

    return appendClock(day, `${clockLabel(start)} ${copy.at} ${clockLabel(end)}`, zone);
  }

  const sameYear = start.day.slice(0, 4) === end.day.slice(0, 4);
  const sameMonth = sameYear && start.month === end.month;
  const startFormat = sameMonth ? 'day' : sameYear ? 'dayMonth' : 'full';
  const span = `${copy.from} ${formatDay(start.day, locale, startFormat)} ${copy.to} ${formatDay(end.day, locale, 'full')}`;

  if (startMidnight && endMidnight) {
    return span;
  }

  if (startMidnight) {
    return appendClock(span, `${copy.at} ${clockLabel(end)}`, zone);
  }

  if (endMidnight) {
    return appendClock(span, clockLabel(start), zone);
  }

  return appendClock(span, `${clockLabel(start)} ${copy.at} ${clockLabel(end)}`, zone);
};

const appendClock = (when: string, clock: string, zone: string): string =>
  zone === '' ? `${when}, ${clock}` : `${when}, ${clock} ${zone}`;

export const pageLocale = (lang: string): string =>
  lang.toLowerCase().startsWith('fr') ? 'fr-CH' : 'en-GB';

export const regionName = (code: string, lang: string): string => {
  if (code === '') {
    return '';
  }

  try {
    return new Intl.DisplayNames([pageLocale(lang)], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
};

export const languageName = (code: string, lang: string): string => {
  if (code === '') {
    return '';
  }

  try {
    const label =
      new Intl.DisplayNames([pageLocale(lang)], { type: 'language' }).of(code) ?? code;
    const locale = pageLocale(lang);
    return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
  } catch {
    return code;
  }
};

type DayFormat = 'day' | 'dayMonth' | 'full';

const formatDay = (day: string, locale: string, format: DayFormat): string => {
  const [year, month, date] = day.split('-').map(Number);
  if (year == null || month == null || date == null) {
    return day;
  }

  const options: Intl.DateTimeFormatOptions =
    format === 'day'
      ? { day: '2-digit' }
      : format === 'dayMonth'
        ? { day: '2-digit', month: 'long' }
        : { day: '2-digit', month: 'long', year: 'numeric' };

  return new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    ...options,
  }).format(new Date(Date.UTC(year, month - 1, date)));
};
